'use client';

import type { ReactNode } from 'react';

import { X } from 'lucide-react';
import { Drawer } from 'vaul';

import { Button } from './button';
import { cn } from '../utils/cn';

interface SideNavDrawerProps {
	open: boolean;
	onClose: () => void;
	/** Accessible (sr-only) drawer title. */
	title: string;
	/** Small uppercase label in the top strip, left of the close button. */
	label?: string;
	/**
	 * Theming overrides for Drawer.Content (background / border, and the `dark`
	 * scope when needed). The vaul portal escapes the page's `.dark` wrapper, so a
	 * dark surface must pass `dark` here or theme tokens resolve to the light theme.
	 */
	className?: string;
	children: ReactNode;
}

/**
 * SideNavDrawer — left-anchored vaul drawer for mobile navigation across public
 * surfaces (docs + marketing). Slides in with vaul's native animation; dismisses
 * on overlay tap/drag or a tap anywhere in the body (so a nav-link tap closes
 * it). The body is the scroll container.
 *
 * Theming is left to `className` so each surface keeps its own look — docs is
 * light (`bg-background`), marketing is dark (`dark bg-black/95 …`).
 */
export const SideNavDrawer = ({
	open,
	onClose,
	title,
	label,
	className,
	children,
}: SideNavDrawerProps): React.JSX.Element => (
	<Drawer.Root open={open} onOpenChange={(next) => !next && onClose()} direction="left" modal>
		<Drawer.Portal>
			<Drawer.Overlay className="fixed inset-0 z-[9999] bg-backdrop/40 backdrop-blur-[2px]" />
			<Drawer.Content
				className={cn(
					'fixed inset-y-0 left-0 z-[9999] flex w-[300px] max-w-[88vw] flex-col shadow-floating focus:outline-none',
					className,
				)}
			>
				<Drawer.Title className="sr-only">{title}</Drawer.Title>
				<div className="flex items-center justify-between px-4 py-3">
					<span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-neutral-muted">
						{label}
					</span>
					<Button
						variant="ghost"
						size="sm"
						iconOnly
						aria-label="Close navigation"
						onClick={onClose}
					>
						<X />
					</Button>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto" onClick={onClose}>
					{children}
				</div>
			</Drawer.Content>
		</Drawer.Portal>
	</Drawer.Root>
);
